import { type ComponentProps } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import Badge from '@atlaskit/badge';
import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import Button, { IconButton } from '@atlaskit/button-v25-3/new';
import EditIcon from '@atlaskit/icon/core/edit';
import LinkIcon from '@atlaskit/icon/core/link';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import Lozenge from '@atlaskit/lozenge-next/new';
import { Tab, TabList } from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';

import { surveyTotals } from '../../jsm-portal-playground-hr-data';
import { jsmPortalPlaygroundHrMessages as messages } from '../../jsm-portal-playground-hr-messages';
import { JsmPlaygroundHeading } from '../jsm-playground-heading';

export const SURVEY_RESPONSES_TAB_TEST_ID = 'jsm-hr-survey-responses-tab';

type ButtonIcon = NonNullable<ComponentProps<typeof Button>['iconBefore']>;
type IconButtonIcon = NonNullable<ComponentProps<typeof IconButton>['icon']>;

const CopyLinkIcon: ButtonIcon = () => <LinkIcon label="" size="medium" />;
const EditButtonIcon: ButtonIcon = () => <EditIcon label="" size="medium" />;
const MoreIcon: IconButtonIcon = () => <ShowMoreHorizontalIcon label="" size="medium" />;

const noop = () => {};

const breadcrumbs = [
	messages.breadcrumbSpaces,
	messages.breadcrumbSpace,
	messages.breadcrumbSurveys,
	messages.surveyTitle,
];

/**
 * Breadcrumbs, the survey's title and status, and the Summary / Responses tab strip. Must render
 * inside `@atlaskit/tabs`' `Tabs`, which supplies the strip's selection.
 *
 * Only the tabs are live. The breadcrumbs and the title's actions exist to make the preview read as
 * the product, so they are drawn inert, as the top navigation's chrome is.
 */
export const SurveyPageHeader = () => {
	const intl = useIntl();

	return (
		<div css={headerStyles}>
			<style>{surveyTabPanelStyle}</style>
			<div css={titleBlockStyles}>
				<div
					aria-hidden="true"
					// @ts-expect-error -- inert is valid HTML but is missing from @compiled/react types
					inert=""
				>
					<Breadcrumbs>
						{breadcrumbs.map((crumb) => (
							<BreadcrumbsItem key={crumb.id} text={intl.formatMessage(crumb)} />
						))}
					</Breadcrumbs>
				</div>

				<div css={titleRowStyles}>
					<span css={titleStyles}>
						<JsmPlaygroundHeading level={2} size="large">
							{intl.formatMessage(messages.surveyTitle)}
						</JsmPlaygroundHeading>
						<Lozenge appearance="success">{intl.formatMessage(messages.statusActive)}</Lozenge>
					</span>
					<span
						css={actionsStyles}
						aria-hidden="true"
						// @ts-expect-error -- inert is valid HTML but is missing from @compiled/react types
						inert=""
					>
						<Button iconBefore={CopyLinkIcon} onClick={noop} tabIndex={-1}>
							{intl.formatMessage(messages.copyLink)}
						</Button>
						<Button iconBefore={EditButtonIcon} onClick={noop} tabIndex={-1}>
							{intl.formatMessage(messages.edit)}
						</Button>
						<IconButton
							icon={MoreIcon}
							label={intl.formatMessage(messages.moreActions)}
							onClick={noop}
							tabIndex={-1}
						/>
					</span>
				</div>
			</div>

			<TabList>
				<Tab>{intl.formatMessage(messages.tabSummary)}</Tab>
				<Tab>
					<span css={tabLabelStyles} data-testid={SURVEY_RESPONSES_TAB_TEST_ID}>
						{intl.formatMessage(messages.tabResponses)}
						<Badge>{surveyTotals.responses}</Badge>
					</span>
				</Tab>
			</TabList>
		</div>
	);
};

// v13 tab panels pad 8px inline, and that rule is as specific as a nested override, so it wins.
// These panel ids belong only to this survey, and an id beats the tabs stylesheet.
const surveyTabPanelStyle = `
#jsm-playground-hr-survey-0-tab,
#jsm-playground-hr-survey-1-tab {
	padding-inline: 0;
}
#jsm-playground-hr-survey-0,
#jsm-playground-hr-survey-1 {
	padding-block: var(--ds-space-075, 6px);
}
`;

const headerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	flexShrink: 0,
	gap: token('space.100'),
	paddingBlockStart: token('space.300'),
	paddingInline: token('space.400'),
	// Tabs 21 pulls the strip left by the tab's own inline padding so "Summary" and the rule
	// start on the title. v13 has no negative margin, and its rule stops 8px short of both edges.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& [role="tablist"]': {
		marginInlineStart: `calc(${token('space.100')} * -1)`,
	},
	// The v13 track is a 2px rule inset 8px on the right. Run it out to the title row's right edge
	// and keep the selected weight so the rule stays a hair heavier than a 1px hairline.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& [role="tablist"]::before': {
		insetInlineEnd: 0,
		height: token('border.width.selected', '2px'),
	},
	// Match the prototype's 14px / 20px Atlassian Sans tabs (wac-web's older @atlaskit/tabs v13 defaults to 16px system font)
	// and its 6px block padding (v13 uses 4px, which shortens the strip).
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& [role="tablist"] [role="tab"]': {
		fontFamily:
			'"Atlassian Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
		fontSize: '14px',
		lineHeight: '20px',
		fontWeight: token('font.weight.medium', '500'),
		paddingBlock: token('space.075', '6px'),
	},
});

const titleBlockStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.050'),
});

const titleRowStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: token('space.100'),
	paddingBlockEnd: token('space.100'),
});

const titleStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.100'),
	minWidth: 0,
});

const actionsStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	flexShrink: 0,
	gap: token('space.100'),
});

const tabLabelStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.050'),
});
